// Cleaning tool item lookup and state persistence.
import {
  ApiCleaningToolBarcodeLookupResponse,
  ApiCleaningToolCropLookupResponse,
  ApiCleaningToolDesignLookupResponse,
  ApiCleaningToolLinkTargetsRequest,
  ApiCleaningToolLinkTargetsResponse,
  ApiCleaningToolUPCData,
  CleaningToolCropQueryResponse,
  CleaningToolStateSaveRequest,
  CleaningToolStateSaveResponse,
} from '../../types/cleaningTool';
import { forgeRequest } from '../forgeRequest';
import { FORGE_CLEANING_TOOL_API_URL } from './endpoints';

export async function fetchCleaningToolCropQuery(
  token: string,
  cropId: string,
  params?: {
    chainId?: number | null;
    limit?: number;
  }
): Promise<CleaningToolCropQueryResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.limit !== undefined) query.set('limit', String(params.limit));

  return forgeRequest<CleaningToolCropQueryResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/crops/${encodeURIComponent(
      cropId
    )}/query${query.toString() ? `?${query.toString()}` : ''}`,
    'Error querying Cleaning crop'
  );
}

export async function fetchItem(
  token: string,
  itemNumber: string,
  chainId: number
): Promise<ApiCleaningToolUPCData> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiCleaningToolUPCData>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/item-number/${encodeURIComponent(itemNumber)}?${params}`,
    'Error fetching item data'
  );
}

export async function fetchItemByBarcode(
  token: string,
  barcodeQuery: string,
  chainId: number
): Promise<ApiCleaningToolBarcodeLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiCleaningToolBarcodeLookupResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
    'Error fetching item data by barcode'
  );
}

export async function fetchItemByDesignId(
  token: string,
  designId: string,
  chainId: number
): Promise<ApiCleaningToolDesignLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiCleaningToolDesignLookupResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/design/${encodeURIComponent(designId)}?${params}`,
    'Error fetching item data by design id'
  );
}

export async function saveState(
  token: string,
  request: CleaningToolStateSaveRequest
): Promise<CleaningToolStateSaveResponse> {
  return forgeRequest<CleaningToolStateSaveResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/state`,
    'Error saving Cleaning state',
    { method: 'PUT', body: request }
  );
}

export async function fetchItemsByCropIds(
  token: string,
  cropIds: string[],
  chainId: number
): Promise<ApiCleaningToolCropLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  cropIds.forEach((cropId) => params.append('crop_id', cropId));
  return forgeRequest<ApiCleaningToolCropLookupResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/crops?${params}`,
    'Error fetching item data by crop ids'
  );
}

export async function resolveCleaningToolLinkTargets(
  token: string,
  request: ApiCleaningToolLinkTargetsRequest
): Promise<ApiCleaningToolLinkTargetsResponse> {
  return forgeRequest<ApiCleaningToolLinkTargetsResponse>(
    token,
    `${FORGE_CLEANING_TOOL_API_URL}/link-targets`,
    'Error resolving Cleaning link targets',
    {
      method: 'POST',
      body: {
        chain_id: request.chainId,
        item_numbers: request.itemNumbers ?? [],
        normalized_barcodes: request.normalizedBarcodes ?? [],
        design_ids: request.designIds ?? [],
        crop_ids: request.cropIds ?? [],
      },
    }
  );
}
